import { useEffect, useState } from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { Autocomplete, Checkbox, FormControlLabel } from "@mui/material";
import colors from "../constants/Colors";
import { ArrowBackIosNew } from "@mui/icons-material";

function Copyright(props) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
    >
      {"Copyright © "}
      <Link color="inherit" href="https://mui.com/">
        Your Website
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

const defaultTheme = createTheme();

export default function Signup({ setToLogin }) {
  const [partners, setPartners] = useState([]);
  const [options, setOptions] = useState([]);
  const [selectedName, setSelectedName] = useState("");
  const [selectedPic, setSelectedPic] = useState("");
  const [manualEntry, setManualEntry] = useState(false);
  const [manualName, setManualName] = useState("");
  const [manualPic, setManualPic] = useState("");

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await axios.get(
          " https://eutracker.rdiup.com/v1/partners/"
        );
        setPartners(response.data);

        // Combine name and pic into a single list of options
        const combinedOptions = response.data.map((partner) => ({
          label: `${partner.name} (PIC: ${partner.pic})`,
          name: partner.name,
          pic: partner.pic,
        }));

        // Remove duplicates (if any)
        setOptions(combinedOptions);
      } catch (err) {
        console.log("Failed to fetch partners", err);
      }
    };

    fetchPartners();
  }, []);

  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Create a FormData object to gather form inputs
    const formData = new FormData(event.currentTarget);
    console.log("pic", selectedPic);
    // Build the data object to send in the request
    const requestData = {
      firstname: formData.get("firstName"), // Updated key
      lastname: formData.get("lastName"), // Updated key
      email: formData.get("email"),
      password: formData.get("password"),
      password2: formData.get("password2"),
      pic_number: manualEntry ? String(manualPic) : selectedPic,
      organisation_name: manualEntry ? manualName : selectedName,
    };

    try {
      const response = await axios.post(
        " https://eutracker.rdiup.com/v1/user/register/",
        requestData
      );
      console.log("Registration successful:", response);
      Swal.fire({
        title: "Success!",
        text: "Registration successful. Redirecting to login...",
        icon: "success",
        confirmButtonText: "OK",
      }); //.then(() => {})
      navigate("/");
    } catch (error) {
      console.error(
        "Registration error:",
        error.response?.data || error.message
      );

      // Extracting the error message
      const errorMessage = error.response?.data?.message || "An error occurred";

      // If there are specific field errors, display them
      const fieldErrors = error.response?.data?.data || {};
      let detailedErrors = "";

      // Define a mapping of field names to user-friendly messages
      const fieldNameMapping = {
        pic_number: "pic number",
        email: "email",
        firstname: "first name",
        lastname: "last name",
        // Add more fields as needed
      };

      // Handle specific field errors
      for (const [field, messages] of Object.entries(fieldErrors)) {
        if (messages.length > 0) {
          const friendlyFieldName = fieldNameMapping[field] || field; // Fallback to the original field name
          // Check for specific messages and handle them accordingly
          messages.forEach((message) => {
            if (message.includes("already exists")) {
              detailedErrors += `${friendlyFieldName} already exists.\n`;
            } else if (message.includes("may not be blank")) {
              detailedErrors += `${friendlyFieldName} may not be blank.\n`;
            } else {
              detailedErrors += `${message}\n`; // Fallback for other messages
            }
          });
        }
      }

      // Handle non-field specific errors (like unique constraint)
      if (error.response?.data?.non_field_errors) {
        error.response.data.non_field_errors.forEach((message) => {
          detailedErrors += `${message}\n`;
        });
      }

      // Show SweetAlert with the appropriate error message
      Swal.fire({
        title: "Error!",
        text: `Registration failed: ${detailedErrors.trim() || errorMessage}`,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: "#1976d2" }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography
            variant="h5"
            style={{ fontWeight: "800", color: "#1976d2" }}
          >
            Sign Up
          </Typography>
          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{ mt: 3 }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  autoComplete="given-name"
                  name="firstName"
                  required
                  fullWidth
                  id="firstName"
                  label="First Name"
                  autoFocus
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="lastName"
                  label="Last Name"
                  name="lastName"
                  autoComplete="family-name"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="new-password"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="password2"
                  label="Confirm password"
                  type="password"
                  id="password2"
                  autoComplete="new-password"
                />
              </Grid>
              {!manualEntry ? (
                <Grid item xs={12}>
                  <Autocomplete
                    options={options}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Organisation" />
                    )}
                    onChange={(event, value) => {
                      if (value) {
                        setSelectedName(value.name);
                        setSelectedPic(value.pic);
                      } else {
                        setSelectedName("");
                        setSelectedPic("");
                      }
                    }}
                  />
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      id="manualName"
                      label="Organization Name"
                      value={manualName}
                      onChange={(e) => setManualName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      id="manualPic"
                      label="PIC Number"
                      type="number"
                      value={manualPic}
                      onChange={(e) => setManualPic(e.target.value)}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={manualEntry}
                      onChange={() => setManualEntry(!manualEntry)}
                    />
                  }
                  label="I didn't find my organization, add manually"
                />
              </Grid>
            </Grid>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, backgroundColor: colors.primary }}
            >
              Sign Up
            </Button>
            <Grid container justifyContent="flex-end">
              <Grid item>
                <Link href="#" variant="body2" onClick={() => setToLogin(true)}>
                  Already have an account? Sign in
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Box>
        {/* <Copyright sx={{ mt: 5 }} /> */}
      </Container>
    </ThemeProvider>
  );
}
